import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import BarChartIcon from '@mui/icons-material/BarChart';
import PageTitle from "../PageTitle";
import EscalationV2Nav from "./EscalationV2Nav";
import EscalationFilterBar from "./EscalationFilterBar";
import AnalyticsSection from "./AnalyticsSection";
import AgentPerformanceSection, { useAgentStats } from "./AgentPerformanceSection";
import LifecycleSection from "./LifecycleSection";
import useEscalationFilterBar from "./useEscalationFilterBar";
import { ANALYTICS_RANGE_MESSAGE, isAnalyticsRangeOk, mergeDrilldownPatch, filtersToSearchParams } from "./escalationFilterUtils";
import { ACCENTS, COLORS, SHADOW } from "./emsTheme";

/**
 * Analytics page — Escalation Management V2's stats view (Phase 6 / CTO point #6), on its own
 * route (/tools/escalation-v2/analytics) rather than embedded above the Tickets list. Separate
 * page per the user's call: at production ticket volumes the breakdown table can run long, and
 * stacking it above the list pushed the list too far down regardless of collapse/scroll
 * treatment — a dedicated page sidesteps that entirely, and the Tabs bar (EscalationV2Nav)
 * keeps the two feeling like one section rather than two unrelated tools.
 *
 * Tiles/breakdown-row clicks drill into specific tickets — since this is a different route than
 * the Tickets page, that means navigating there with the resulting filters in the query string
 * (see escalationFilterUtils), not a local state update like a same-page embed could do.
 */
const VIEWS = [
    { key: 'overview', label: 'Overview' },
    { key: 'agents', label: 'Assignee performance' },
    { key: 'lifecycle', label: 'Ticket lifecycle' },
];

const AnalyticsPage = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    // Analytics opens on the last 30 days: one day is too little to show a trend. Reset goes back to it.
    const bar = useEscalationFilterBar(undefined, { defaultDays: 30 });
    const { appliedFilters, setAppliedFilters } = bar;

    // Every report here needs a bounded range (see ANALYTICS_MAX_RANGE_DAYS); a longer one, e.g. from
    // the picker's "days up to today" box, is turned down here rather than sent.
    const onSubmit = (values) => {
        const next = bar.applyFilters(values);
        if (!isAnalyticsRangeOk(next)) {
            enqueueSnackbar(ANALYTICS_RANGE_MESSAGE, { variant: 'warning' });
            return;
        }
        setAppliedFilters(next);
    };
    const onReset = () => bar.resetFilters();

    // Which report, kept in the URL (?view=agents) so a report can be bookmarked or shared.
    const [searchParams, setSearchParams] = useSearchParams();
    const view = VIEWS.some((v) => v.key === searchParams.get('view')) ? searchParams.get('view') : 'overview';
    const setView = (key) => {
        const next = new URLSearchParams(searchParams);
        if (key === 'overview') next.delete('view'); else next.set('view', key);
        setSearchParams(next, { replace: true });
    };
    const [groupBy, setGroupBy] = useState('severity');
    const agentStats = useAgentStats(appliedFilters, groupBy, view !== 'overview');

    const onDrilldown = (patch) => {
        const filters = mergeDrilldownPatch(appliedFilters, patch);
        setAppliedFilters(filters);
        navigate(`/tools/escalation-v2?${filtersToSearchParams(filters).toString()}`);
    };

    return (
        <>
            <PageTitle>Escalation Management V2: Analytics</PageTitle>

            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                    width: 48, height: 48, borderRadius: '12px', flexShrink: 0,
                    background: ACCENTS.purple.grad, boxShadow: `0 6px 16px ${COLORS.purple}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <BarChartIcon sx={{ color: '#fff', fontSize: 26 }} />
                </Box>
                <Box>
                    <Typography component="h1" sx={{ fontSize: 22, fontWeight: 800, color: COLORS.ink, lineHeight: 1.3, letterSpacing: '-.01em' }}>
                        Escalation analytics
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: COLORS.ink2 }}>
                        Is the ticket backlog under control, where is TAT being missed, and who needs help.
                    </Typography>
                </Box>
            </Box>

            <EscalationV2Nav />
            <EscalationFilterBar
                bar={bar} couriers={bar.couriers} onSubmit={onSubmit} onReset={onReset}
                hideFields={['ticketId', 'awb']} themed
            />
            <Box sx={{ overflowX: 'auto', mb: 2.5 }}>
                <Box role="tablist" aria-label="Analytics views" sx={{ display: 'inline-flex', gap: 0.5, p: 0.5, borderRadius: '999px', bgcolor: COLORS.surface, border: `1px solid ${COLORS.grid}`, boxShadow: SHADOW }}>
                    {VIEWS.map((v) => (
                        <Box key={v.key} component="button" type="button" role="tab" aria-selected={view === v.key} onClick={() => setView(v.key)}
                            sx={{ all: 'unset', px: 2, py: 0.875, fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', borderRadius: '999px',
                                color: view === v.key ? '#fff' : COLORS.ink3,
                                background: view === v.key ? ACCENTS.purple.grad : 'transparent',
                                boxShadow: view === v.key ? `0 4px 12px ${COLORS.purple}40` : 'none',
                                transition: 'color 150ms, background-color 150ms',
                                '&:hover': view === v.key ? {} : { color: COLORS.purple, bgcolor: COLORS.purpleSoft },
                                '&:focus-visible': { outline: `2px solid ${COLORS.purple}`, outlineOffset: 2 } }}>
                            {v.label}
                        </Box>
                    ))}
                </Box>
            </Box>
            {view === 'overview' && <AnalyticsSection filters={appliedFilters} onDrilldown={onDrilldown} bar={bar} />}
            {view === 'agents' && (
                <AgentPerformanceSection data={agentStats.data} loading={agentStats.loading} filters={appliedFilters}
                    groupBy={groupBy} setGroupBy={setGroupBy} onDrilldown={onDrilldown} />
            )}
            {view === 'lifecycle' && <LifecycleSection data={agentStats.data} loading={agentStats.loading} filters={appliedFilters} statusList={bar.statusList} />}
        </>
    );
};

export default AnalyticsPage;
